import { ChangeEvent, FC, forwardRef, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useCombinedRefs } from '@utils';

import { RadioGroupProps } from './types';
import { Check } from './styled';

const Component: FC<RadioGroupProps> = memo<RadioGroupProps>(
    forwardRef((props, ref) => {
        const { id, value, name, disabled, options, errorText, required, validator, onChange, inline = false } = props;

        const [builtInErrorMessage, setErrorMessage] = useState(''),
            radioGroupId = useMemo(() => id || name, [id, name]),
            radioGroupRef = useCombinedRefs<HTMLDivElement>(ref, useRef(null)),
            hasError = useMemo(() => !!errorText || !!builtInErrorMessage, [errorText, builtInErrorMessage]);

        const validate = useCallback(
            (selectedValue: string, validationMessage?: string) =>
                setErrorMessage((validator && validator(selectedValue)) || validationMessage || ''),
            [validator],
        );

        const handleOnChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target;
                let _value: string | number = value;
                if (!Number.isNaN(Number(_value))) _value = Number(_value);
                validate(value);
                onChange && onChange(_value);
            },
            [validate, onChange],
        );

        return (
            <div ref={radioGroupRef} id={`${radioGroupId}-wrapper`} aria-describedby={`${radioGroupId}-helper-text`}>
                <div id={`${radioGroupId}-options`} className="d-flex">
                    {options.map((option) => (
                        <Check
                            className="d-flex flex-column-reverse align-items-center"
                            key={option.value}
                            name={name}
                            label={option.label}
                            value={option.value}
                            type="radio"
                            checked={option.value === value}
                            onChange={handleOnChange}
                            disabled={disabled || option.disabled}
                            isInvalid={hasError}
                            id={`${option.label}-${radioGroupId}`}
                            required={required}
                            inline={inline}
                        />
                    ))}
                </div>
            </div>
        );
    }),
);

Component.displayName = 'RadioGroup';
export const RadioGroup: FC<RadioGroupProps> = Object.assign(Component, {});
